import { Button } from '@edsn/pie-components';
import { useEffect, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { CancelDataServicesRequest } from '#pie/components/cancel-data-services-request/CancelDataServicesRequest';
import { Card } from '#pie/components/card/Card';
import { Page, PageHeader } from '#pie/components/page/Page';
import { Stack } from '#pie/components/stack/Stack';
import { useToast } from '#pie/components/toast/ToastContext';
import {
  type CreateSimpleDataRequest,
  usePostSimpleDataRequest,
} from '#pie/internal/api/pie-bff';
import { SimpleAttributes } from './fields/SimpleAttributes';
import { SimpleClarification } from './fields/SimpleClarification';
import { SimpleDataRequestTo } from './fields/SimpleDataRequestTo';
import { SimpleDataTypes } from './fields/SimpleDataTypes';
import { SimpleFrequencyRequest } from './fields/SimpleFrequency';
import { SimplePeriods } from './fields/SimplePeriods';
import { SimpleReason } from './fields/SimpleReason';

export const SimpleDataServicesForm = () => {
  const { t } = useTranslation();
  const { t: tdr } = useTranslation('dataRequest');
  const navigate = useNavigate();
  const { addToast } = useToast();
  const formHelpers = useForm<CreateSimpleDataRequest>();
  const [shouldBlock, setShouldBlock] = useState(true);

  /* istanbul ignore next -- @preserve */
  const { mutateAsync, isSuccess } = usePostSimpleDataRequest({
    mutation: {
      onError: (error) => {
        addToast({
          message: tdr('toast.error.message'),
          title: tdr('toast.error.title'),
          type: 'error',
          error: error,
        });
      },
      onSuccess: () => {
        setShouldBlock(false);
        addToast({
          message: tdr('toast.success.message'),
          title: tdr('toast.success.title'),
          type: 'success',
        });
      },
    },
  });

  useEffect(() => {
    if (isSuccess) navigate('/datadiensten');
  }, [isSuccess]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const onSubmit: SubmitHandler<CreateSimpleDataRequest> = async (data) => {
    setIsSubmitting(true);
    mutateAsync({
      data,
    });
  };

  return (
    <Page>
      <PageHeader
        title={tdr('heading')}
        backOnClick={() => navigate('/datadiensten')}
        previousText={tdr('button_back')}
      />
      <FormProvider {...formHelpers}>
        <form onSubmit={formHelpers.handleSubmit(onSubmit)}>
          <Card size="md" className="mx-auto max-w-4xl">
            <Stack gap="lg" divider="line">
              <Stack gap="md">
                <SimpleReason />
                <SimpleDataRequestTo />
                <SimpleDataTypes />
                <SimplePeriods />
                <SimpleFrequencyRequest />
                <SimpleAttributes />
                <SimpleClarification />
              </Stack>
              <footer className="flex justify-between gap-x-4">
                <Button as={Link} to=".." action="tertiary" emphasis="subtle">
                  {t('common.button.cancel')}
                </Button>
                <Button isLoading={isSubmitting} type="submit">
                  {t('common.submit')}
                </Button>
              </footer>
            </Stack>
          </Card>
          <CancelDataServicesRequest shouldBlock={shouldBlock} />
        </form>
      </FormProvider>
    </Page>
  );
};
